// frontend/src/hooks/useChunkedUpload.ts
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const formatBytes = (bytes: number) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

export const formatDuration = (sec: number) => {
  if (!sec || sec <= 0) return '< 1 Min';
  if (sec < 60) return `${Math.ceil(sec)} Sek`;
  if (sec < 3600) return `${Math.ceil(sec / 60)} Min`;
  return `${(sec / 3600).toFixed(1)} Std`;
};

// ─── Types ────────────────────────────────────────────────────────────────────
export interface UploadQueueItem {
  id: string;
  file: File;
  status: 'queued' | 'awaiting' | 'uploading' | 'done' | 'error' | 'cancelled';
  percent: number;
  uploadedParts: number;
  totalParts: number;
  partSize: number;
  startTime?: number;
  errorMsg?: string;
  uploadId?: string;
}

export interface BatchConfirmState {
  items: { itemId: string; file: File; existingFile: any | null }[];
  storageStats: any | null;
  resolve: (decisions: { itemId: string; mode: 'new' | 'version' | 'overwrite' | 'skip' }[] | null) => void;
}

type EntityParams = {
  buildingId?: string;
  plotId?: string;
  projectId?: string;
  customerId?: string;
};

interface UseChunkedUploadOptions {
  entityParams: EntityParams;
  onComplete: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useChunkedUpload({ entityParams, onComplete }: UseChunkedUploadOptions) {
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [batchConfirm, setBatchConfirm] = useState<BatchConfirmState | null>(null);
  const [batchDecisions, setBatchDecisions] = useState<Record<string, 'new' | 'version' | 'overwrite' | 'skip'>>({});
  const abortControllers = useRef<Record<string, AbortController>>({});

  const token = localStorage.getItem('token');
  const apiUrl = process.env.REACT_APP_API_URL;
  const headers = { Authorization: `Bearer ${token}` };

  const updateQueueItem = useCallback((id: string, update: Partial<UploadQueueItem>) => {
    setUploadQueue(q => q.map(item => item.id === id ? { ...item, ...update } : item));
  }, []);

  // ─── Single file upload (chunked multipart) ────────────────────────────────
  const uploadSingleFile = useCallback(async (itemId: string, file: File, mode: 'new' | 'version' | 'overwrite') => {
    const overwrite = mode === 'overwrite';

    const controller = new AbortController();
    abortControllers.current[itemId] = controller;

    const startResp = await axios.post(`${apiUrl}/api/files/chunked/start`, {
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      ...entityParams,
      overwrite
    }, { headers });

    const { uploadId, partSize, totalParts, partUrls } = startResp.data;
    updateQueueItem(itemId, { totalParts, partSize, uploadId });

    let completed = 0;
    let next = 0;

    const worker = async () => {
      while (next < totalParts) {
        if (controller.signal.aborted) return;
        const partIndex = next++;
        const partNumber = partIndex + 1;
        const blob = file.slice(partIndex * partSize, Math.min((partIndex + 1) * partSize, file.size));
        const url = partUrls[partIndex];

        let lastErr: any = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const r = await fetch(url, { method: 'PUT', body: blob, signal: controller.signal });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            lastErr = null;
            break;
          } catch (err: any) {
            if (err.name === 'AbortError') throw err;
            lastErr = err;
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          }
        }
        if (lastErr) throw new Error(`Part ${partNumber}: ${lastErr.message}`);

        completed++;
        updateQueueItem(itemId, { uploadedParts: completed, percent: Math.round((completed / totalParts) * 100) });
      }
    };

    try {
      await Promise.all([worker(), worker(), worker(), worker(), worker()]);
      await axios.post(`${apiUrl}/api/files/chunked/${uploadId}/complete`, {}, { headers });
    } catch (err: any) {
      if (err.name === 'AbortError') {
        try { await axios.delete(`${apiUrl}/api/files/chunked/${uploadId}`, { headers }); } catch {}
        throw new Error('UPLOAD_CANCELLED');
      }
      throw err;
    } finally {
      delete abortControllers.current[itemId];
    }
  }, [apiUrl, entityParams, headers, updateQueueItem]);

  // ─── Process files (batch check + upload) ──────────────────────────────────
  const processFiles = useCallback(async (fileArray: File[]) => {
    if (fileArray.length === 0) return;

    const items: UploadQueueItem[] = fileArray.map(f => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      status: 'queued' as const,
      percent: 0, uploadedParts: 0, totalParts: 0, partSize: 0
    }));
    setUploadQueue(items);
    setUploading(true);

    // Phase 1: Existence checks
    const checkResults: { itemId: string; file: File; existingFile: any | null }[] = [];
    for (const item of items) {
      updateQueueItem(item.id, { status: 'awaiting' });
      try {
        const r = await axios.post(
          `${apiUrl}/api/files/check`,
          { name: item.file.name, ...entityParams },
          { headers }
        );
        checkResults.push({ itemId: item.id, file: item.file, existingFile: r.data.exists ? r.data.file : null });
      } catch {
        checkResults.push({ itemId: item.id, file: item.file, existingFile: null });
      }
    }

    // Phase 2: Batch decision dialog
    const needsDecision = checkResults.some(r => r.existingFile !== null || r.file.size > 1024 * 1024 * 1024);
    let modeMap = new Map<string, 'new' | 'version' | 'overwrite' | 'skip'>();

    if (needsDecision) {
      let storageStats: any = null;
      try {
        const r = await axios.get(`${apiUrl}/api/files/chunked/storage-stats`, { headers });
        storageStats = r.data;
      } catch {}

      const initialDecisions: Record<string, 'new' | 'version' | 'overwrite' | 'skip'> = {};
      checkResults.forEach(r => {
        initialDecisions[r.itemId] = r.existingFile ? 'version' : 'new';
      });
      setBatchDecisions(initialDecisions);

      const decisions = await new Promise<{ itemId: string; mode: 'new' | 'version' | 'overwrite' | 'skip' }[] | null>(resolve => {
        setBatchConfirm({ items: checkResults, storageStats, resolve });
      });
      setBatchConfirm(null);
      setBatchDecisions({});

      if (!decisions) {
        items.forEach(i => updateQueueItem(i.id, { status: 'cancelled' }));
        setUploading(false);
        setTimeout(() => setUploadQueue([]), 3000);
        return;
      }

      decisions.forEach(d => modeMap.set(d.itemId, d.mode));
    } else {
      checkResults.forEach(r => modeMap.set(r.itemId, 'new'));
    }

    // Phase 3: Upload all
    for (const item of items) {
      const mode = modeMap.get(item.id);
      if (!mode || mode === 'skip') {
        updateQueueItem(item.id, { status: 'cancelled' });
        continue;
      }
      try {
        updateQueueItem(item.id, { status: 'uploading', startTime: Date.now() });
        await uploadSingleFile(item.id, item.file, mode as 'new' | 'version' | 'overwrite');
        updateQueueItem(item.id, { status: 'done', percent: 100 });
      } catch (err: any) {
        if (err.message !== 'UPLOAD_CANCELLED') {
          updateQueueItem(item.id, { status: 'error', errorMsg: err.message });
        }
      }
    }

    onComplete();
    setUploading(false);
    setTimeout(() => setUploadQueue([]), 3000);
  }, [apiUrl, entityParams, headers, updateQueueItem, uploadSingleFile, onComplete]);

  // ─── Public API ────────────────────────────────────────────────────────────
  const handleUpload = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    await processFiles(Array.from(selectedFiles));
  }, [processFiles]);

  const applyBulkDecision = useCallback((mode: 'version' | 'overwrite' | 'skip') => {
    if (!batchConfirm) return;
    const newDecisions = { ...batchDecisions };
    batchConfirm.items.forEach(it => {
      if (it.existingFile) newDecisions[it.itemId] = mode;
    });
    setBatchDecisions(newDecisions);
  }, [batchConfirm, batchDecisions]);

  const cancelUpload = useCallback((itemId: string) => {
    if (abortControllers.current[itemId]) {
      abortControllers.current[itemId].abort();
    }
    updateQueueItem(itemId, { status: 'cancelled' });
  }, [updateQueueItem]);

  return {
    uploading,
    uploadQueue,
    batchConfirm,
    batchDecisions,
    setBatchDecisions,
    handleUpload,
    applyBulkDecision,
    cancelUpload,
    formatBytes,
    formatDuration,
  };
}
